import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiJson } from '@/lib/apiClient';
import { isNotFound } from '@/features/trips/useTrips';
import { localDayKey, localDayRange } from '@/features/trips/format';
import { isOverviewSummary, type OverviewSummary } from './types';

/**
 * The Overview summary (slice 15) and the window it is asked for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "TODAY" IS A CIVIL DAY, AND A CIVIL DAY ONLY EXISTS IN A TIME ZONE.
 * ─────────────────────────────────────────────────────────────────────────────
 * `todayRange` resolves the window in the TENANT's zone, through the very same
 * `localDayKey`/`localDayRange` the Trips page uses — not `new Date(y, m, d)`,
 * which yields the VIEWING MACHINE's midnight. That was a real defect on the
 * trips surface: days resolved in EAT on Mahad's phone, UTC on CI, and wrong for
 * a Zambian tenant. Reading the browser zone here would reintroduce it on the
 * landing screen, where it is least likely to be noticed, because in Mogadishu
 * the two agree and everything looks right.
 *
 * The discriminating case, and the one the test uses: at 22:30 UTC the tenant
 * zone `Africa/Mogadishu` (UTC+3) is already 01:30 the NEXT day. A correct
 * implementation asks for a window starting at 21:00 UTC; a browser/UTC one asks
 * for one starting at 00:00 UTC. Under any daytime timestamp the two agree and a
 * test would prove nothing (CLAUDE.md — "would the correct and the incorrect
 * implementation produce the same answer here?").
 *
 * The window END is `now`, not the tenant's midnight-to-come: the tile says
 * "today", and today has not finished. Asking to the end of the day would sweep
 * in nothing extra now, but it would start doing so the moment a future-dated
 * row can exist, and "today" would quietly begin meaning "this calendar day
 * including hours that have not happened".
 */
export function todayRange(nowMs: number, timeZone: string): { from: string; to: string } {
  const range = localDayRange(localDayKey(nowMs, timeZone), timeZone);
  const now = new Date(nowMs).toISOString();
  // `localDayRange` returns null only for a malformed day key, which cannot come
  // from `localDayKey`. Falling back to `now` for both bounds keeps the query
  // valid (and empty) rather than sending `undefined` to a required param.
  return { from: range ? range.from : now, to: now };
}

export const overviewQueryKey = (from: string, to: string) =>
  ['overview', from, to] as const;

/**
 * `GET /api/overview?from&to`.
 *
 * SEPARATE FROM THE LIVE QUERY ON PURPOSE. The vehicle counts on this screen come
 * from `useLiveVehicles` (Redis, via `/api/vehicles/live`); these come from
 * Postgres. Keeping them as two queries means a liveness outage degrades the live
 * tiles WITHOUT blanking the alert and distance ones, which are unaffected by it —
 * and withholding numbers we do have would be its own kind of dishonesty.
 *
 * `refetchInterval` is deliberately slow (60 s). These are day totals: they move
 * on the scale of trips and crossings, not seconds, and this is the screen every
 * session starts on.
 */
export function useOverview(
  range: { from: string; to: string },
  opts: { enabled?: boolean } = {},
): UseQueryResult<OverviewSummary> {
  const { from, to } = range;
  return useQuery({
    queryKey: overviewQueryKey(from, to),
    enabled: opts.enabled ?? true,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ from, to });
      const body = await apiJson<unknown>(`/api/overview?${params.toString()}`, { signal });
      // `apiJson` casts, it does not verify — see `isOverviewSummary`. A body of
      // the wrong shape becomes a query ERROR here, so the tiles render "—" and
      // the rest of the Overview keeps working. Before this guard it threw inside
      // render and took the whole screen with it.
      if (!isOverviewSummary(body)) throw new Error('overview: unexpected response shape');
      return body;
    },
    // A 400/404 is a definitive answer about the request, not a transient failure.
    retry: (count, error) => !isNotFound(error) && count < 2,
    refetchInterval: 60_000,
  });
}
